"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Sparkles, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { getTranslations } from "@/lib/translations"
import type { Locale } from "@/lib/i18n"

interface DreamAssistantPreviewProps {
  locale: Locale
  randomImage?: string | null
}

export function DreamAssistantPreview({ locale, randomImage: initialRandomImage }: DreamAssistantPreviewProps) {
  const t = getTranslations(locale)
  const currentLocale = locale
  const [randomImage, setRandomImage] = useState<string | null>(initialRandomImage || null)
  
  // Client-side'da da rastgele görsel çek
  useEffect(() => {
    if (!randomImage) {
      fetch('/api/ai/get-random-image')
        .then(res => res.json())
        .then(data => {
          if (data.success && data.imageUrl) {
            setRandomImage(data.imageUrl)
          }
        })
        .catch(error => {
          console.error('Error fetching random image:', error)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  // Rastgele görsel varsa onu kullan, yoksa varsayılan görseli kullan
  const imageSrc = randomImage || '/ai.jpg'

  return (
    <section className="relative overflow-hidden pt-0 pb-0">
      <div className="relative w-full min-h-[400px] sm:min-h-[500px] lg:min-h-[500px] overflow-hidden bg-[#034AA6]" suppressHydrationWarning>
        {/* Mobile: Background Image */}
        <div className="absolute inset-0 lg:hidden" suppressHydrationWarning>
          <Image
            src={imageSrc}
            alt={currentLocale === 'tr' ? 'Hayalini Anlat' : 'Tell Your Dream'}
            fill
            className="object-cover"
            quality={75}
            priority
            sizes="(max-width: 640px) 100vw, 640px"
            unoptimized={imageSrc.startsWith('http')}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-[#034AA6]/90" suppressHydrationWarning />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(3,74,166,0.2),transparent_70%)]" suppressHydrationWarning />
        </div>

        {/* Desktop: Sağ Taraf Arka Plan Görseli */}
        <div className="hidden lg:block absolute inset-0" suppressHydrationWarning>
          <div className="absolute left-1/2 top-0 bottom-0 right-0" suppressHydrationWarning>
            <Image
              src={imageSrc}
              alt={currentLocale === 'tr' ? 'Hayalini Anlat' : 'Tell Your Dream'}
              fill
              className="object-cover"
              quality={80}
              sizes="(max-width: 1920px) 50vw, 960px"
              unoptimized={imageSrc.startsWith('http')}
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-l from-[#034AA6]/80 via-[#034AA6]/40 to-transparent" suppressHydrationWarning />
          </div>
        </div>

        {/* Desktop: Grid Layout */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 h-full" suppressHydrationWarning>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-center min-h-[400px] sm:min-h-[500px] lg:min-h-[500px] py-8 sm:py-12 lg:py-16" suppressHydrationWarning>
            {/* Sol Taraf - İçerik */}
            <div className="text-center lg:text-left text-white flex flex-col justify-center relative z-10 min-h-[400px] sm:min-h-[500px] lg:min-h-0" suppressHydrationWarning>
              <div className="flex items-center gap-3 mb-4 justify-center lg:justify-start">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Sparkles className="h-8 w-8" />
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold font-mono tracking-tight">
                  {t.dreamAssistant?.title || (currentLocale === 'tr' ? 'Hayalini Anlat' : 'Tell Your Dream')}
                </h2>
              </div>

              <p className="text-base sm:text-lg text-white/90 mb-6 sm:mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {t.dreamAssistant?.description || (currentLocale === 'tr' 
                  ? 'Projenizi anlatın, AI ile analiz edelim ve size özel çözümler sunalım.'
                  : 'Describe your project, let AI analyze it and offer you customized solutions.')}
              </p>

              {/* Features */}
              <div className="space-y-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-3 text-left">
                  <div className="p-2 bg-white/20 rounded-lg flex-shrink-0 backdrop-blur-sm">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg mb-1">
                      {currentLocale === 'tr' ? 'AI Destekli Analiz' : 'AI-Powered Analysis'}
                    </h3>
                    <p className="text-sm sm:text-base text-white/90">
                      {currentLocale === 'tr' 
                        ? 'Projeniz AI tarafından analiz edilir ve size özel öneriler sunulur.'
                        : 'Your project is analyzed by AI and personalized recommendations are provided.'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 text-left">
                  <div className="p-2 bg-white/20 rounded-lg flex-shrink-0 backdrop-blur-sm">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg mb-1">
                      {currentLocale === 'tr' ? 'Görsel Oluşturma' : 'Image Generation'}
                    </h3>
                    <p className="text-sm sm:text-base text-white/90">
                      {currentLocale === 'tr' 
                        ? 'Projeniz için AI ile görsel oluşturulur.'
                        : 'AI-generated visuals are created for your project.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4" suppressHydrationWarning>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white text-[#034AA6] hover:bg-[#034AA6] hover:text-white px-6 sm:px-8 py-4 sm:py-5 h-auto rounded-lg font-semibold text-sm sm:text-base shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300"
                >
                  <Link href={`/${locale}/${locale === 'tr' ? 'hayalini-anlat' : 'dream-assistant'}`} className="flex items-center">
                    {currentLocale === 'tr' ? 'Hayalini Anlat' : 'Tell Your Dream'}
                    <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Sağ Taraf - Boş (Görsel arka planda) */}
            <div className="hidden lg:block" suppressHydrationWarning></div>
          </div>
        </div>
      </div>
    </section>
  )
}

